import {
  AnalyzerType,
  ZyusetuFieldKeyFragmentFragment,
  ZyusetuFieldKeyType,
  ZyusetuFieldValueAnalyzedPayloadFragmentFragment
} from "@/graphql/graphql.ts";
import {FormSchema} from "@/components/pages/Zyusetu/ZyusetuEditorSection.tsx";
import {ZyusetuEditType} from "@/types/ZyusetuEditType.ts";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {Box, Button, Card, Divider, FormHelperText, Grid2, Stack, TextField, Tooltip, Typography} from "@mui/material";
import {Plus, X} from "lucide-react";
import {zyusetuValueIntoZodValue} from "@/hooks/useGetFirstContent.ts";
import {useCallback} from "react";

interface ZyusetuPremisesLandRightsFieldProps {
  fieldKey: ZyusetuFieldKeyFragmentFragment;
  onFocus: (
    value?: ZyusetuFieldValueAnalyzedPayloadFragmentFragment | null,
  ) => void,
  updateField: (
    keyId: string,
    keyName: string,
    value: FormSchema<ZyusetuFieldKeyType.PremisesLandRights>[string],
  ) => Promise<void>;
  isAvailable: boolean;
  editType?: ZyusetuEditType
}

interface PremisesLandRightsValue {
  index: number
  address: string
  chiban: string
  chimoku: string
  chiseki: string
  rightType: string
  rightsRatio: string
}

export function ZyusetuPremisesLandRightsField(
  {
    fieldKey,
    updateField,
    isAvailable,
    onFocus,
    editType
  }: ZyusetuPremisesLandRightsFieldProps
) {
  const {
    control,
    setValue,
    getValues,
    formState: {errors}
  } = useFormContext<FormSchema<ZyusetuFieldKeyType.PremisesLandRights>>();
  const watch = useWatch({
    name: `${fieldKey.keyName}.value`,
    control
  });
  
  const onFieldFocus = useCallback((
    index: number, keyName: string
  ) => {
    return () => {
      const payload = fieldKey.analyzedPayloads?.find((pay) => pay.type === AnalyzerType.DocumentAi)
      
      if (!payload) {
        return
      }
      const currentIndex = getValues(`${fieldKey.keyName}.value.${index}.index`)
      const idx: number = payload.properties.find((p: any) => p.keyName === "index" && p.content === currentIndex.toString())?.idx
      if (idx === null) {
        return;
      }
      
      const pageAnchor = payload.properties.find((p: any) => p.keyName === keyName && p.idx === idx)?.pageAnchor
      if (pageAnchor === null) {
        return;
      }
      
      onFocus({
        id: payload.id,
        createdAt: payload.id,
        analyzedFileId: payload.analyzedFileId,
        pageAnchor,
        type: AnalyzerType.DocumentAi,
      })
    }
  }, [fieldKey.analyzedPayloads, fieldKey.keyName, getValues, onFocus])
  
  const handleAppend = () => {
    const currentValues = getValues(`${fieldKey.keyName}.value`) as PremisesLandRightsValue[] || [];
    setValue(`${fieldKey.keyName}.value`, [
      ...currentValues,
      {
        index: currentValues.length + 1,
        address: '',
        chiban: '',
        chimoku: '',
        chiseki: '',
        rightType: '',
        rightsRatio: ''
      }
    ]);
  };
  
  const handleRemove = (index: number) => {
    const currentValues = getValues(`${fieldKey.keyName}.value`) as PremisesLandRightsValue[] || [];
    setValue(
      `${fieldKey.keyName}.value`,
      currentValues.filter((_, i) => i !== index)
    );
  };
  
  const values = watch || [];
  
  return (
    <>
      <Card sx={{p: 2}}>
        
        {values.map((_, index) => (
          <>
            {index != 0 &&
              <Divider sx={{
                my: 3
              }}/>}
            <Stack direction={'row'} key={`premises-land-rights-${index}`} spacing={2}>
              <Box>
                
                <Grid2 container spacing={2} key={`${fieldKey.keyName}-tenant-input-${index}`}
                       sx={{width: '100%'}}>
                  <Grid2 size={2}>
                    <Typography variant="formLabel"
                                sx={{
                                  borderLeft: '5px solid',
                                  borderColor: "primary.main",
                                  pl: 1,
                                }}>
                      符号
                    </Typography>
                    <Controller
                      name={`${fieldKey.keyName}.value.${index}.index`}
                      control={control}
                      render={({field: fieldRender}) => (
                        <TextField
                          {...fieldRender}
                          onFocus={onFieldFocus(index, "index")}
                          placeholder={"符号"}
                          fullWidth
                          error={!!errors?.[fieldKey.keyName]?.value?.[index]?.address?.message}
                          helperText={errors?.[fieldKey.keyName]?.value?.[index]?.address?.message as string}
                          disabled={!isAvailable}
                          size={'small'}
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={7}>
                    <Typography variant="formLabel"
                                sx={{
                                  borderLeft: '5px solid',
                                  borderColor: "primary.main",
                                  pl: 1,
                                }}>
                      所在
                    </Typography>
                    <Controller
                      name={`${fieldKey.keyName}.value.${index}.address`}
                      control={control}
                      render={({field: fieldRender}) => (
                        <TextField
                          {...fieldRender}
                          onFocus={onFieldFocus(index, "address")}
                          placeholder={"所在"}
                          fullWidth
                          error={!!errors?.[fieldKey.keyName]?.value?.[index]?.address?.message}
                          helperText={errors?.[fieldKey.keyName]?.value?.[index]?.address?.message as string}
                          disabled={!isAvailable}
                          size={'small'}
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={3}>
                    <Typography variant="formLabel">
                      地番
                    </Typography>
                    <Controller
                      name={`${fieldKey.keyName}.value.${index}.chiban`}
                      control={control}
                      render={({field: fieldRender}) => (
                        <TextField
                          {...fieldRender}
                          onFocus={onFieldFocus(index, "chiban")}
                          placeholder={"地番"}
                          error={!!errors?.[fieldKey.keyName]?.value?.[index]?.chiban?.message}
                          helperText={errors?.[fieldKey.keyName]?.value?.[index]?.chiban?.message as string}
                          fullWidth
                          disabled={!isAvailable}
                          size={'small'}
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={3}>
                    <Typography variant="formLabel">
                      地目（登記）
                    </Typography>
                    <Controller
                      name={`${fieldKey.keyName}.value.${index}.chimoku`}
                      control={control}
                      render={({field: fieldRender}) => (
                        <TextField
                          {...fieldRender}
                          onFocus={onFieldFocus(index, "chimoku")}
                          placeholder={"地目"}
                          error={!!errors?.[fieldKey.keyName]?.value?.[index]?.chimoku?.message}
                          helperText={errors?.[fieldKey.keyName]?.value?.[index]?.chimoku?.message as string}
                          fullWidth
                          disabled={!isAvailable}
                          size={'small'}
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={3}>
                    <Typography variant="formLabel">
                      地積（登記）
                    </Typography>
                    <Controller
                      name={`${fieldKey.keyName}.value.${index}.chiseki`}
                      control={control}
                      render={({field: fieldRender}) => (
                        <TextField
                          {...fieldRender}
                          placeholder={"地積"}
                          onFocus={onFieldFocus(index, "chiseki")}
                          error={!!errors?.[fieldKey.keyName]?.value?.[index]?.chiseki?.message}
                          helperText={errors?.[fieldKey.keyName]?.value?.[index]?.chiseki?.message as string}
                          fullWidth
                          disabled={!isAvailable}
                          size={'small'}
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={3}>
                    <Typography variant="formLabel">
                      敷地権の種類
                    </Typography>
                    <Controller
                      name={`${fieldKey.keyName}.value.${index}.rightType`}
                      control={control}
                      render={({field: fieldRender}) => (
                        <TextField
                          {...fieldRender}
                          placeholder={"種類"}
                          onFocus={onFieldFocus(index, "rightType")}
                          error={!!errors?.[fieldKey.keyName]?.value?.[index]?.rightType?.message}
                          helperText={errors?.[fieldKey.keyName]?.value?.[index]?.rightType?.message as string}
                          fullWidth
                          disabled={!isAvailable}
                          size={'small'}
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={3}>
                    <Typography variant="formLabel">
                      敷地権の割合
                    </Typography>
                    <Controller
                      name={`${fieldKey.keyName}.value.${index}.rightsRatio`}
                      control={control}
                      render={({field: fieldRender}) => (
                        <TextField
                          {...fieldRender}
                          placeholder={"敷地権"}
                          onFocus={onFieldFocus(index, "rightsRatio")}
                          error={!!errors?.[fieldKey.keyName]?.value?.[index]?.rightsRatio?.message}
                          helperText={errors?.[fieldKey.keyName]?.value?.[index]?.rightsRatio?.message as string}
                          fullWidth
                          disabled={!isAvailable}
                          size={'small'}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              </Box>
              <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Tooltip title={'削除'}>
                  <Button
                    tabIndex={-1}
                    sx={{width: "80px"}}
                    variant={'outlined'}
                    onClick={() => handleRemove(index)}
                    disabled={!isAvailable || editType === ZyusetuEditType.ReadOnly}
                    size={'small'}
                  >
                    削除<X/>
                  </Button>
                </Tooltip>
              </Box>
            </Stack>
          
          </>
        ))}
        <Box sx={{textAlign: 'center', mt: 3}}>
          <Button
            variant={'contained'}
            tabIndex={-1}
            onClick={handleAppend}
            disabled={values.length >= 6 || editType === ZyusetuEditType.ReadOnly}
          >
            <Plus/>
            追加
          </Button>
          <Button
            disabled={editType === ZyusetuEditType.ReadOnly}
            variant={'text'}
            tabIndex={-1}
            onClick={async () => {
              if (errors?.[fieldKey.keyName]?.value?.message) return;
              
              await updateField(
                fieldKey.id,
                fieldKey.keyName,
                zyusetuValueIntoZodValue(
                  fieldKey,
                  watch
                ) as FormSchema<ZyusetuFieldKeyType.PremisesLandRights>[string]
              );
            }}
          >
            保存
          </Button>
        </Box>
        {errors?.[fieldKey.keyName]?.value?.message &&
          <FormHelperText style={{color: "red"}}>
            {errors?.[fieldKey.keyName]?.value?.message || ""}
          </FormHelperText>}
      </Card>
    </>
  );
}
